import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import { generateNewPageable, isPageTooHigh } from 'src/adminPanel/utils/helperClasses';
import {
    createNewEventCommissionModel,
    createNewEventEvent,
    createNewEventLocation,
    createNewEventParticipant,
    createNewEventType,
    deactivateEventCommissionModel,
    deactivateEventLanguageCountryModel,
    deactivateEventLocation,
    deactivateEventType,
    deleteEventEvent,
    deleteEventParticipant,
    fetchEventCommissionModels,
    fetchEventEvents,
    fetchEventLanguageCountryModels,
    fetchEventLocations,
    fetchEventParticipant,
    fetchEventTypes,
    updateEventCommissionModel,
    updateEventEvent,
    updateEventLocation,
    updateEventParticipant,
    updateEventType,
} from 'src/api/event-admin';
import {
    CREATE_NEW_EVENT_ADMIN,
    CREATE_NEW_EVENT_COMMISSION_MODEL_ADMIN,
    CREATE_NEW_EVENT_LOCATION_ADMIN,
    CREATE_NEW_EVENT_PARTICIPANT_ADMIN,
    CREATE_NEW_EVENT_TYPE_ADMIN,
    DEACTIVATE_EVENT_COMMISSION_MODEL_ADMIN,
    DEACTIVATE_EVENT_LANGUAGE_COUNTRY_MODEL_ADMIN,
    DEACTIVATE_EVENT_LOCATION_ADMIN,
    DEACTIVATE_EVENT_TYPE_ADMIN,
    DELETE_EVENT_ADMIN,
    DELETE_EVENT_PARTICIPANT_ADMIN,
    fetchEventCommissionModelsAdmin,
    fetchEventLanguageCountryModelsAdmin,
    fetchEventLocationsAdmin,
    fetchEventParticipantsAdmin,
    fetchEventsAdmin,
    fetchEventTypesAdmin,
    FETCH_EVENTS_ADMIN,
    FETCH_EVENT_COMMISSION_MODELS_ADMIN,
    FETCH_EVENT_LANGUAGE_COUNTRY_MODELS_ADMIN,
    FETCH_EVENT_LOCATIONS_ADMIN,
    FETCH_EVENT_PARTICIPANTS,
    FETCH_EVENT_TYPES_ADMIN,
    requestCreateNewEventAdmin,
    requestCreateNewEventAdminFailure,
    requestCreateNewEventAdminSuccess,
    requestCreateNewEventCommissionModelAdmin,
    requestCreateNewEventCommissionModelAdminFailure,
    requestCreateNewEventCommissionModelAdminSuccess,
    requestCreateNewEventLocationAdmin,
    requestCreateNewEventLocationAdminFailure,
    requestCreateNewEventLocationAdminSuccess,
    requestCreateNewEventParticipantAdmin,
    requestCreateNewEventParticipantAdminFailure,
    requestCreateNewEventParticipantAdminSuccess,
    requestCreateNewEventTypeAdmin,
    requestCreateNewEventTypeAdminFailure,
    requestCreateNewEventTypeAdminSuccess,
    requestDeactivateEventCommissionModelAdmin,
    requestDeactivateEventCommissionModelAdminFailure,
    requestDeactivateEventCommissionModelAdminSuccess,
    requestDeactivateEventLocationAdminFailure,
    requestDeactivateEventLocationAdminSuccess,
    requestDeactivateEventTypeAdmin,
    requestDeactivateEventTypeAdminFailure,
    requestDeactivateEventTypeAdminSuccess,
    requestDeleteEventAdmin,
    requestDeleteEventAdminFailure,
    requestDeleteEventAdminSuccess,
    requestDeleteEventLanguageCountryModelAdmin,
    requestDeleteEventLanguageCountryModelAdminFailure,
    requestDeleteEventLanguageCountryModelAdminSuccess,
    requestDeleteEventParticipantAdmin,
    requestDeleteEventParticipantAdminFailure,
    requestDeleteEventParticipantAdminSuccess,
    requestEventCommissionModelsAdmin,
    requestEventCommissionModelsAdminFailure,
    requestEventCommissionModelsAdminSuccess,
    requestEventLanguageCountryModelsAdmin,
    requestEventLanguageCountryModelsAdminFailure,
    requestEventLanguageCountryModelsAdminSuccess,
    requestEventLocationsAdmin,
    requestEventLocationsAdminFailure,
    requestEventLocationsAdminSuccess,
    requestEventParticipantsAdmin,
    requestEventParticipantsAdminFailure,
    requestEventParticipantsAdminSuccess,
    requestEventsAdmin,
    requestEventsAdminFailure,
    requestEventsAdminSuccess,
    requestEventTypesAdmin,
    requestEventTypesAdminFailure,
    requestEventTypesAdminSuccess,
    requestUpdateEventAdmin,
    requestUpdateEventAdminFailure,
    requestUpdateEventAdminSuccess,
    requestUpdateEventCommissionModelAdmin,
    requestUpdateEventCommissionModelAdminFailure,
    requestUpdateEventCommissionModelAdminSuccess,
    requestUpdateEventLocationAdmin,
    requestUpdateEventLocationAdminFailure,
    requestUpdateEventLocationAdminSuccess,
    requestUpdateEventParticipantAdmin,
    requestUpdateEventParticipantAdminFailure,
    requestUpdateEventParticipantAdminSuccess,
    requestUpdateEventTypeAdmin,
    requestUpdateEventTypeAdminFailure,
    requestUpdateEventTypeAdminSuccess,
    UPDATE_EVENT_ADMIN,
    UPDATE_EVENT_COMMISSION_MODEL_ADMIN,
    UPDATE_EVENT_LOCATION_ADMIN,
    UPDATE_EVENT_PARTICIPANT_ADMIN,
    UPDATE_EVENT_TYPE_ADMIN,
} from './actions';

function* fetchEventEventsSaga(action: AnyAction): SagaIterator {
    yield put(requestEventsAdmin());

    const { error, response } = yield call(fetchEventEvents, action.pageable, action.filter);

    if (error) {
        yield put(requestEventsAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventsAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventsAdminSuccess(response));
        }
    }
}

function* fetchCreateNewEventSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewEventAdmin());

    const { error, response } = yield call(createNewEventEvent, action.eventRequestDto);

    if (error) {
        yield put(requestCreateNewEventAdminFailure(error));
    } else {
        yield put(requestCreateNewEventAdminSuccess(response));
    }
}

function* fetchUpdateEventSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateEventAdmin());

    const { error, response } = yield call(updateEventEvent, action.eventRequestDto);

    if (error) {
        yield put(requestUpdateEventAdminFailure(error));
    } else {
        yield put(requestUpdateEventAdminSuccess(response));
    }
}

function* fetchDeleteEventSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteEventAdmin());

    const { error, response } = yield call(deleteEventEvent, action.eventId);

    if (error) {
        yield put(requestDeleteEventAdminFailure(error));
    } else {
        yield put(requestDeleteEventAdminSuccess(response));
    }
}

export function* generateFetchEventEventsWatcher() {
    yield takeLatest(FETCH_EVENTS_ADMIN, fetchEventEventsSaga);
}

export function* generateFetchCreateNewEventWatcher() {
    yield takeLatest(CREATE_NEW_EVENT_ADMIN, fetchCreateNewEventSaga);
}

export function* generateFetchUpdateEventWatcher() {
    yield takeLatest(UPDATE_EVENT_ADMIN, fetchUpdateEventSaga);
}

export function* generateFetchDeleteEventWatcher() {
    yield takeLatest(DELETE_EVENT_ADMIN, fetchDeleteEventSaga);
}

/* Event Commission Model */
function* fetchEventCommissionModelsSaga(action: AnyAction): SagaIterator {
    yield put(requestEventCommissionModelsAdmin());

    const { error, response } = yield call(fetchEventCommissionModels, action.pageable, action.filter);

    if (error) {
        yield put(requestEventCommissionModelsAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventCommissionModelsAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventCommissionModelsAdminSuccess(response));
        }
    }
}

function* fetchCreateNewEventCommissionModelSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewEventCommissionModelAdmin());

    const { error, response } = yield call(createNewEventCommissionModel, action.commissionModel);

    if (error) {
        yield put(requestCreateNewEventCommissionModelAdminFailure(error));
    } else {
        yield put(requestCreateNewEventCommissionModelAdminSuccess(response));
    }
}

function* fetchUpdateEventCommissionModelSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateEventCommissionModelAdmin());

    const { error, response } = yield call(updateEventCommissionModel, action.commissionModel);

    if (error) {
        yield put(requestUpdateEventCommissionModelAdminFailure(error));
    } else {
        yield put(requestUpdateEventCommissionModelAdminSuccess(response));
    }
}

function* fetchDeactivateEventCommissionModelSaga(action: AnyAction): SagaIterator {
    yield put(requestDeactivateEventCommissionModelAdmin());

    const { error, response } = yield call(deactivateEventCommissionModel, action.modelId);

    if (error) {
        yield put(requestDeactivateEventCommissionModelAdminFailure(error));
    } else {
        yield put(requestDeactivateEventCommissionModelAdminSuccess(response));
    }
}

export function* generateFetchEventCommissionModelsWatcher() {
    yield takeLatest(FETCH_EVENT_COMMISSION_MODELS_ADMIN, fetchEventCommissionModelsSaga);
}

export function* generateFetchCreateNewEventCommissionModelWatcher() {
    yield takeLatest(CREATE_NEW_EVENT_COMMISSION_MODEL_ADMIN, fetchCreateNewEventCommissionModelSaga);
}

export function* generateFetchUpdateEventCommissionModelWatcher() {
    yield takeLatest(UPDATE_EVENT_COMMISSION_MODEL_ADMIN, fetchUpdateEventCommissionModelSaga);
}

export function* generateFetchDeleteEventCommissionModelWatcher() {
    yield takeLatest(DEACTIVATE_EVENT_COMMISSION_MODEL_ADMIN, fetchDeactivateEventCommissionModelSaga);
}

/* Event Location */
function* fetchEventLocationsSaga(action: AnyAction): SagaIterator {
    yield put(requestEventLocationsAdmin());

    const { error, response } = yield call(fetchEventLocations, action.pageable, action.filter);

    if (error) {
        yield put(requestEventLocationsAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventLocationsAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventLocationsAdminSuccess(response));
        }
    }
}

function* fetchCreateNewEventLocationSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewEventLocationAdmin());

    const { error, response } = yield call(createNewEventLocation, action.eventLocation);

    if (error) {
        yield put(requestCreateNewEventLocationAdminFailure(error));
    } else {
        yield put(requestCreateNewEventLocationAdminSuccess(response));
    }
}

function* fetchUpdateEventLocationSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateEventLocationAdmin());

    const { error, response } = yield call(updateEventLocation, action.eventLocation);

    if (error) {
        yield put(requestUpdateEventLocationAdminFailure(error));
    } else {
        yield put(requestUpdateEventLocationAdminSuccess(response));
    }
}

function* fetchDeactivateEventLocationSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteEventAdmin());

    const { error, response } = yield call(deactivateEventLocation, action.modelId);

    if (error) {
        yield put(requestDeactivateEventLocationAdminFailure(error));
    } else {
        yield put(requestDeactivateEventLocationAdminSuccess(response));
    }
}

export function* generateFetchEventLocationsWatcher() {
    yield takeLatest(FETCH_EVENT_LOCATIONS_ADMIN, fetchEventLocationsSaga);
}

export function* generateFetchCreateNewEventLocationWatcher() {
    yield takeLatest(CREATE_NEW_EVENT_LOCATION_ADMIN, fetchCreateNewEventLocationSaga);
}

export function* generateFetchUpdateEventLocationWatcher() {
    yield takeLatest(UPDATE_EVENT_LOCATION_ADMIN, fetchUpdateEventLocationSaga);
}

export function* generateFetchDeactivateEventLocationWatcher() {
    yield takeLatest(DEACTIVATE_EVENT_LOCATION_ADMIN, fetchDeactivateEventLocationSaga);
}

/* Event Language Country Model */
function* fetchEventLanguageCountryModelsSaga(action: AnyAction): SagaIterator {
    yield put(requestEventLanguageCountryModelsAdmin());

    const { error, response } = yield call(fetchEventLanguageCountryModels, action.pageable, action.filter);

    if (error) {
        yield put(requestEventLanguageCountryModelsAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventLanguageCountryModelsAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventLanguageCountryModelsAdminSuccess(response));
        }
    }
}

function* fetchDeactivateEventLanguageCountryModelSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteEventLanguageCountryModelAdmin());

    const { error, response } = yield call(deactivateEventLanguageCountryModel, action.modelId);

    if (error) {
        yield put(requestDeleteEventLanguageCountryModelAdminFailure(error));
    } else {
        yield put(requestDeleteEventLanguageCountryModelAdminSuccess(response));
    }
}

export function* generateFetchEventLanguageCountryModelWatcher() {
    yield takeLatest(FETCH_EVENT_LANGUAGE_COUNTRY_MODELS_ADMIN, fetchEventLanguageCountryModelsSaga);
}

export function* generateFetchDeactivateEventLanguageCountryModelWatcher() {
    yield takeLatest(DEACTIVATE_EVENT_LANGUAGE_COUNTRY_MODEL_ADMIN, fetchDeactivateEventLanguageCountryModelSaga);
}

/* Event Type */
function* fetchEventTypesSaga(action: AnyAction): SagaIterator {
    yield put(requestEventTypesAdmin());

    const { error, response } = yield call(fetchEventTypes, action.pageable, action.filter);

    if (error) {
        yield put(requestEventTypesAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventTypesAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventTypesAdminSuccess(response));
        }
    }
}

function* fetchCreateNewEventTypesSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewEventTypeAdmin());

    const { error, response } = yield call(createNewEventType, action.eventTypeRequest);

    if (error) {
        yield put(requestCreateNewEventTypeAdminFailure(error));
    } else {
        yield put(requestCreateNewEventTypeAdminSuccess(response));
    }
}

function* fetchUpdateEventTypeSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateEventTypeAdmin());

    const { error, response } = yield call(updateEventType, action.eventTypeRequest);

    if (error) {
        yield put(requestUpdateEventTypeAdminFailure(error));
    } else {
        yield put(requestUpdateEventTypeAdminSuccess(response));
    }
}

function* fetchDeactivateEventTypeSaga(action: AnyAction): SagaIterator {
    yield put(requestDeactivateEventTypeAdmin());

    const { error, response } = yield call(deactivateEventType, action.eventTypeId);

    if (error) {
        yield put(requestDeactivateEventTypeAdminFailure(error));
    } else {
        yield put(requestDeactivateEventTypeAdminSuccess(response));
    }
}

export function* generateFetchEventTypesWatcher() {
    yield takeLatest(FETCH_EVENT_TYPES_ADMIN, fetchEventTypesSaga);
}

export function* generateFetchCreateNewEventTypeWatcher() {
    yield takeLatest(CREATE_NEW_EVENT_TYPE_ADMIN, fetchCreateNewEventTypesSaga);
}

export function* generateFetchUpdateEventTypeWatcher() {
    yield takeLatest(UPDATE_EVENT_TYPE_ADMIN, fetchUpdateEventTypeSaga);
}

export function* generateFetchDeactivateEventTypeWatcher() {
    yield takeLatest(DEACTIVATE_EVENT_TYPE_ADMIN, fetchDeactivateEventTypeSaga);
}

/* Event Participant */
function* fetchEventParticipantsSaga(action: AnyAction): SagaIterator {
    yield put(requestEventParticipantsAdmin());

    const { error, response } = yield call(fetchEventParticipant, action.eventId, action.pageable);

    if (error) {
        yield put(requestEventParticipantsAdminFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchEventParticipantsAdmin(generateNewPageable(action.pageable), action.filter));
        } else {
            yield put(requestEventParticipantsAdminSuccess(response));
        }
    }
}

function* fetchCreateNewEventParticipantSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewEventParticipantAdmin());

    const { error, response } = yield call(createNewEventParticipant, action.eventParticipantRequest);

    if (error) {
        yield put(requestCreateNewEventParticipantAdminFailure(error));
    } else {
        yield put(requestCreateNewEventParticipantAdminSuccess(response));
    }
}

function* fetchUpdateEventParticipantSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateEventParticipantAdmin());

    const { error, response } = yield call(updateEventParticipant, action.udpateEventParticipantRequest);

    if (error) {
        yield put(requestUpdateEventParticipantAdminFailure(error));
    } else {
        yield put(requestUpdateEventParticipantAdminSuccess(response));
    }
}

function* fetchDeleteEventParticipantSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteEventParticipantAdmin());

    const { error, response } = yield call(deleteEventParticipant, action.eventParticipantId);

    if (error) {
        yield put(requestDeleteEventParticipantAdminFailure(error));
    } else {
        yield put(requestDeleteEventParticipantAdminSuccess(response));
    }
}

export function* generateFetchEventParticipantsWatcher() {
    yield takeLatest(FETCH_EVENT_PARTICIPANTS, fetchEventParticipantsSaga);
}

export function* generateFetchCreateNewEventParticipantWatcher() {
    yield takeLatest(CREATE_NEW_EVENT_PARTICIPANT_ADMIN, fetchCreateNewEventParticipantSaga);
}

export function* generateFetchUpdateEventParticipantWatcher() {
    yield takeLatest(UPDATE_EVENT_PARTICIPANT_ADMIN, fetchUpdateEventParticipantSaga);
}

export function* generateFetchDeleteEventParticipantWatcher() {
    yield takeLatest(DELETE_EVENT_PARTICIPANT_ADMIN, fetchDeleteEventParticipantSaga);
}
